import { Component, Inject, Input, Optional, OnInit } from "@angular/core";
import { BaseFormComponent } from "../../../../shared/components/base/base-form.component";
import { UntypedFormArray, UntypedFormBuilder, UntypedFormGroup } from "@angular/forms";
import { MasterModel } from "../../../../master/models/master.model";
import { PreinvoiceService } from "../../../../../modules/invoice/preinvoice/preinvoice.service";
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActualSelected, Utils } from "../../../../../shared/utils";
import { ContactService } from "../../../../../modules/contact/contact/contact.service";
import { ContactFormV2Component } from "../../../../contacts/components/contact-form-v2/contact-form-v2.component";
import { consts } from "../../../../../consts";
import { debounceTime, takeUntil } from "rxjs/operators";
import { firstValueFrom, Subject } from "rxjs";
import { PreinvoiceLookupsFacade } from "../../models/preinvoice-lookups.facade";
import { PreinvoiceFormFactory } from "../../models/preinvoice-form.factory";
import { PreinvoiceFormUtils } from "../../models/preinvoice-form.utils";
import { PreinvoicePaymentValidators } from "../../models/preinvoice-payment.validators";
import { PreinvoiceSaveMapper } from "../../models/preinvoice-save.mapper";
import moment from "moment";
import { ActivityService } from "../../../../../modules/user/activity/activity.service";
import { MasterConceptModel } from "../../../../master/models/master-concept.model";
import { ConceptSerialNumberComponent } from "../../../../../modules/concept/concept/serialNumber/concept-serialNumber.component";
import { ConceptService } from "../../../../../modules/concept/concept/concept.service";
import { UserContactService } from "../../../../../modules/user/usercontact/usercontact.service";
import { ActivatedRoute, Router } from "@angular/router";
import { PreinvoiceFormAdapter } from "../../models/preinvoice-form.adapter";
import { map, delay } from 'rxjs/operators';
import { LoadingDialogService } from "../../../../../shared/ui/loading/loading-dialog.service";

@Component({
    selector: 'app-preinvoice-form',
    templateUrl: './preinvoice-form.components.html',
    styleUrls: ['./preinvoice-form.components.css'],
    standalone: false
})

export class PreinvoiceFormV2Component extends BaseFormComponent implements OnInit {
  @Input() mode: 'page' | 'modal' | 'panel' = 'page';

  actions: any[] = [
    { label: 'Guardar', icon: 'save', variant: 'primary', loading: false, onClick: this.onSave.bind(this) },
  ];

  form: UntypedFormGroup;

  isSaving = false;
  isDeleting = false;
  isLoading = true;
  submitted = false;
  isEditRoute = false;

  contacts: MasterModel[] = [];
  payMethods: MasterModel[] = [];
  bankAccounts: MasterModel[] = [];
  cashRegisters: MasterModel[] = [];
  partners: MasterModel[] = [];
  taxes: MasterModel[] = [];
  concepts: MasterModel[] = [];
  projects: MasterModel[] = [];
  isProjectEnable = false;
  unitMeasurable: MasterModel[] = [];
  accountingModels: MasterModel[] = [];
  centers: MasterModel[] = [];
  userCompany: any;

  // Taxes por defecto del contacto
  private contactTaxClientUid: string | null = null;
  private contactTaxClientSecondUid: string | null = null;
  private contactExpirationDays: number | null = null;
  private readonly lazyLookups = new Map<string, Promise<void>>();

  private destroy$ = new Subject<void>();

  subtotal = 0;
  taxesTotal = 0;
  retentionsTotal = 0;
  total = 0;
  taxesLabel = 'Impuestos';
  lineTotals: number[] = [];
  private taxById = new Map<any, any>();
  private readonly CHUNK = 30;
  visibleCount = this.CHUNK;

  constructor(
    private fb: UntypedFormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private preinvoiceService: PreinvoiceService,
    protected snackBar: MatSnackBar,
    protected dialog: MatDialog,
    private lookupsFacade: PreinvoiceLookupsFacade,
    @Optional() @Inject(MAT_DIALOG_DATA) public data: any,
    @Optional() public dialogRef: MatDialogRef<PreinvoiceFormV2Component>,

    //OLD
    private contactService: ContactService,
    private activityService: ActivityService,
    private conceptService: ConceptService,
    private userContactService: UserContactService,
    private loading: LoadingDialogService,
  ) {
    super(dialog, snackBar);

    if (data && data.mode) {
      this.mode = data.mode;
    }

    if (this.data && this.data.form) {
      this.form = this.data.form;
    } else {
      this.form = PreinvoiceFormFactory.buildForm(this.fb);
      this.setIssuer();
    }
  }

  async ngOnInit() {
    this.loadLookups();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  getData() {
    let self = this;
    this.route.params.subscribe(params => {
      const uid = params['uid'];
      self.isEditRoute = uid != null;
      if (uid != null) {
        self.preinvoiceService.loadData(uid)
          .then(data => self.loadDataAndApplyToForm(data))
          .catch(() => {
            self.isLoading = false;
            self.notification.errorBottom('No se pudo cargar el presupuesto.');
            self.router.navigate(['invoice/preinvoice']);
          });
      } else if (self.data && self.data.form) {
        self.form = self.data.form;
        self.loadActivity();

        // Verificar si el contacto del formulario existe
        const contactUid = self.form.get('contactUid');
        if (contactUid && contactUid.value && contactUid.value.id) {
          const contactExists = self.contacts.some(c => c.id === contactUid.value.id);
          if (!contactExists) {
            // Si el contacto no existe, llamar a onContactChange para manejarlo como contacto nuevo
            self.onContactChange(contactUid.value);
            self.isLoading = false;
          } else {
            self.assingDataContact();
            self.isLoading = false;
          }
        } else if (contactUid && contactUid.value && contactUid.value.display) {
          self.onContactChange({ id: contactUid.value.display, display: contactUid.value.display });
          self.isLoading = false;
        }

        return;
      }
      else {
        self.loadActivity();
      }
    });
  }

  private async loadDataAndApplyToForm(api: any): Promise<void> {
    await Promise.all([
      api && api.receiverUid ? this.loadCentersForContactAsync(api.receiverUid) : Promise.resolve(),
      this.ensurePaymentLookups(api ? api.typePay : ''),
      api && api.projectUid ? this.ensureProjects() : Promise.resolve(),
    ]);

    this.applyApiToForm(api);
    this.isLoading = false;
  }

  get items(): UntypedFormArray {
    return PreinvoiceFormFactory.items(this.form);
  }

  get visibleItems(): any[] {
    return this.items.controls.slice(0, this.visibleCount);
  }

  onLinesScroll(ev: any): void {
    const el = ev && ev.target;
    if (!el) return;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 200 && this.visibleCount < this.items.length) {
      this.visibleCount = Math.min(this.visibleCount + this.CHUNK, this.items.length);
    }
  }

  private loadLookups(): void {
    let self = this;
    this.isLoading = true;

    this.lookupsFacade.loadAll$()
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        function (l: any) {
          self.contacts = (l && l.contacts) ? l.contacts : [];
          self.payMethods = (l && l.payMethods) ? l.payMethods : [];
          self.bankAccounts = (l && l.bankAccounts) ? l.bankAccounts : [];
          self.cashRegisters = (l && l.cashRegisters) ? l.cashRegisters : [];
          self.partners = (l && l.partners) ? l.partners : [];
          self.taxes = (l && l.taxes) ? l.taxes : [];
          self.buildTaxIndex();
          self.concepts = (l && l.concepts) ? l.concepts : [];
          self.projects = (l && l.projects) ? l.projects : [];
          self.unitMeasurable = (l && l.unitMeasurable) ? l.unitMeasurable : [];
          self.accountingModels = (l && l.accountingModels) ? l.accountingModels : [];
          self.centers = (l && l.centers) ? l.centers : [];
          self.userCompany = (l && l.userCompany) ? l.userCompany : null;

          if (ActualSelected && ActualSelected.iaeIdHeading === consts.IAE_ID_VEHICLES) {
            self.form.patchValue({ applyKm: true });
          }

          self.initFromData();

          self.form.get('issuerName').setValue(self.userCompany.companyName);
          self.form.get('issuerVatNumber').setValue(self.userCompany.vatNumber);
          self.applyDefaultEntranceAccount();

          self.wirePaymentValidators();
          self.wireExpiration();
          self.ensureOneLine();
          self.wireTotals();
          self.recomputeTotals();

          self.getData();

        },
        function (err: any) {
          console.error(err);
          self.isLoading = false;
          self.notification.errorBottom('Error cargando datos del formulario.');
        }
      );
  }

  private initFromData(): void {
    if (!this.data) {
      return;
    }
    if (this.data.uid) {
      return;
    }

    if (this.data.form) {
      this.form = this.data.form;
      return;
    }
  }

  applyDefaultEntranceAccount() {
    const self = this;

    if (Utils.IsNullOrEmpty(self.form.get('entranceAccount').value)) {
      let account = null;
      if (this.userCompany.isSalesInvoice) {
        account = self.accountingModels.filter(c => c.metadata != null && c.metadata.number == '7000000001')[0];
      }

      if (this.userCompany.isServiceInvoice) {
        account = self.accountingModels.filter(c => c.metadata != null && c.metadata.number == '7050000001')[0];
      }

      if (this.userCompany.isSalesInvoice && this.userCompany.isServiceInvoice) {
        account = self.accountingModels.filter(c => c.metadata != null && c.metadata.number == '7000000001')[0];
      }

      if (account) {
        self.form.patchValue({ entranceAccount: { id: account.id, display: account.display } });
      }

    }
  }

  private applyApiToForm(api: any): void {
    let mapped = PreinvoiceFormAdapter.toFormPatch({
      api: api,
      payMethods: this.payMethods,
      taxes: this.taxes,
      concepts: this.concepts,
      contacts: this.contacts,
      cashRegisters: this.cashRegisters,
      bankAccounts: this.bankAccounts,
      partners: this.partners,
      projects: this.projects,
      accountingModels: this.accountingModels,
      masterUnitsMeasurables: this.unitMeasurable,
      centers: this.centers
    });

    this.form.patchValue(mapped.patch, { emitEvent: false });
    this.visibleCount = this.CHUNK;

    let arr = this.items;
    arr.clear({ emitEvent: false });

    for (let i = 0; i < mapped.items.length; i++) {
      let it = mapped.items[i];

      if (it.type === 'title') {
        let fgTitle = PreinvoiceFormFactory.createTitle(this.fb);
        fgTitle.patchValue({ title: it.title || '' }, { emitEvent: false });
        arr.push(fgTitle, { emitEvent: false });
      } else {
        let fgLine = PreinvoiceFormFactory.createLine(this.fb);
        fgLine.patchValue(it, { emitEvent: false });
        arr.push(fgLine, { emitEvent: false });
      }
    }
    this.recomputeTotals();
  }

  private setIssuer() {
    let self = this;

    this.userContactService.loadData().then(userContact => {
      if (Utils.IsNullOrEmpty(self.form.get('issuerAddress').value)) self.form.get('issuerAddress').setValue(userContact.address);
      if (Utils.IsNullOrEmpty(self.form.get('issuerPostalCode').value)) self.form.get('issuerPostalCode').setValue(userContact.postalCode);
      if (Utils.IsNullOrEmpty(self.form.get('issuerLocation').value)) self.form.get('issuerLocation').setValue(userContact.location);
      if (Utils.IsNullOrEmpty(self.form.get('issuerCountry').value)) self.form.get('issuerCountry').setValue(userContact.country);
      if (Utils.IsNullOrEmpty(self.form.get('issuerState').value)) self.form.get('issuerState').setValue(userContact.state);
    });
  }

  private ensureOneLine(): void {
    if (this.items.length === 0) this.addLine();
  }

  // ----------------------------
  // Form actions
  // ----------------------------
  addTitle(): void {
    this.items.push(PreinvoiceFormFactory.createTitle(this.fb));
    this.visibleCount = this.items.length;
    this.recomputeTotals();
  }

  addLine(): void {
    this.items.push(PreinvoiceFormFactory.createLine(this.fb));
    this.visibleCount = this.items.length;
    this.recomputeTotals();
  }

  removeLine(i: number): void {
    this.items.removeAt(i);
    this.recomputeTotals();
  }

  dropLine(event: any): void {
    let moved = this.items.at(event.previousIndex);
    this.items.removeAt(event.previousIndex);
    this.items.insert(event.currentIndex, moved);
    this.recomputeTotals();
    //   dropLine(event: CdkDragDrop<FormGroup[]>): void {
    //   moveItemInArray(this.lines.controls, event.previousIndex, event.currentIndex);
    //   this.lines.updateValueAndValidity();
    // }
  }
  // ----------------------------
  // Payment validators wiring
  // ----------------------------
  private wirePaymentValidators(): void {
    let self = this;
    let payCtrl = this.form.get('payMethodUid');
    if (!payCtrl) return;

    payCtrl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(function () {
      self.applyPaymentValidators();
      self.ensurePaymentLookups(self.getPayTypeLabel());
    });

    this.applyPaymentValidators();
  }

  private applyPaymentValidators(): void {
    let payLabel = this.getPayTypeLabel();
    PreinvoicePaymentValidators.apply(this.form, payLabel, consts);
  }

  private wireExpiration(): void {
    const releaseCtrl = this.form.get('invoiceRelease');
    const expirationCtrl = this.form.get('invoiceExpiration');
    if (!releaseCtrl || !expirationCtrl) return;

    releaseCtrl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((release: any) => {
      if (!release) return;
      const releaseDate = moment(release);
      if (!releaseDate.isValid()) return;

      if (this.contactExpirationDays != null) {
        expirationCtrl.setValue(releaseDate.clone().add(this.contactExpirationDays, 'days').toDate());
        return;
      }

      const expiration = expirationCtrl.value ? moment(expirationCtrl.value) : null;
      if (!expiration || !expiration.isValid() || expiration.isBefore(releaseDate, 'day')) {
        expirationCtrl.setValue(releaseDate.toDate());
      }
    });
  }

  get expirationBeforeRelease(): boolean {
    const release = this.form.get('invoiceRelease').value;
    const expiration = this.form.get('invoiceExpiration').value;
    if (!release || !expiration) return false;
    return moment(expiration).isBefore(moment(release), 'day');
  }

  private hasConceptLines(): boolean {
    const items = this.items.controls || [];
    return items.some(item => item.get('type') && item.get('type').value === 'line');
  }

  private showValidationErrors(): void {
    const messages = PreinvoiceFormFactory.getValidationMessages(this.form);
    if (!this.hasConceptLines()) messages.unshift('Añade al menos una línea de concepto.');
    if (this.expirationBeforeRelease) messages.push('La fecha de expiración no puede ser anterior a la de creación.');

    const shown = messages.slice(0, 6).map(message => '• ' + message);
    const rest = messages.length - shown.length;
    if (rest > 0) shown.push(`• y ${rest} más`);
    this.notification.errorBottom(shown.length ? 'Revisa los campos marcados en rojo:\n' + shown.join('\n') : 'Revisa los campos marcados en rojo.');

    this.visibleCount = this.items.length;
    setTimeout(() => {
      const firstInvalid = document.querySelector('app-preinvoice-form .mat-form-field-invalid, app-preinvoice-form .mat-mdc-form-field-invalid, app-preinvoice-form .pf-empty-lines--invalid');
      if (firstInvalid) firstInvalid.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
  }

  private ensureLookup(key: string, load: () => Promise<void>): Promise<void> {
    let entry = this.lazyLookups.get(key);
    if (!entry) {
      entry = load().catch(() => {
        this.lazyLookups.delete(key);
      });
      this.lazyLookups.set(key, entry);
    }
    return entry;
  }

  private ensurePaymentLookups(payLabel: string): Promise<void> {
    const tasks: Promise<void>[] = [];
    if (PreinvoicePaymentValidators.isBankAccountPayment(payLabel, consts)) {
      tasks.push(this.ensureLookup('bankAccounts', () => firstValueFrom(this.lookupsFacade.loadBankAccounts$()).then(list => { this.bankAccounts = list || []; })));
    }
    if (PreinvoicePaymentValidators.isCashRegisterPayment(payLabel)) {
      tasks.push(this.ensureLookup('cashRegisters', () => firstValueFrom(this.lookupsFacade.loadCashRegisters$()).then(list => { this.cashRegisters = list || []; })));
    }
    if (PreinvoicePaymentValidators.isPartnerPayment(payLabel)) {
      tasks.push(this.ensureLookup('partners', () => firstValueFrom(this.lookupsFacade.loadPartners$()).then(list => { this.partners = list || []; })));
    }
    return Promise.all(tasks).then(() => undefined);
  }

  private ensureProjects(): Promise<void> {
    return this.ensureLookup('projects', () => firstValueFrom(this.lookupsFacade.loadProjects$()).then(list => { this.projects = list || []; }));
  }

  private getPayTypeLabel(): string {
    let pm = PreinvoiceFormUtils.resolveSelected(this.payMethods, this.form.value.payMethodUid);
    if (!pm) return '';
    return pm.display || pm.name || '';
  }

  isBankAccountPayment(): boolean {
    if (!this.form.value.payMethodUid) return false;
    return PreinvoicePaymentValidators.isBankAccountPayment(this.form.value.payMethodUid.id, consts);
  }

  isCashRegisterPayment(): boolean {
    if (!this.form.value.payMethodUid) return false;
    return PreinvoicePaymentValidators.isCashRegisterPayment(this.form.value.payMethodUid.id);
  }

  isPartnerPayment(): boolean {
    if (!this.form.value.payMethodUid) return false;
    return PreinvoicePaymentValidators.isPartnerPayment(this.form.value.payMethodUid.id);
  }

  // ----------------------------
  // Save
  // ----------------------------
  onSave(): void {
    let self = this;
    if (this.isSaving) return;

    this.submitted = true;
    this.applyPaymentValidators();
    this.form.markAllAsTouched();

    if (this.form.invalid || this.expirationBeforeRelease || !this.hasConceptLines()) {
      this.showValidationErrors();
      return;
    }

    this.isSaving = true;

    try {
      let fv = this.form.getRawValue();
      this.recomputeTotals();

      if (!Utils.DateIsInFiscalYear(fv.invoiceRelease)) {
        throw new Error('La fecha de creación y el año fiscal seleccionado no son el mismo.');
      }
      if (Utils.IsNullOrEmpty(fv.entranceAccount)) {
        throw new Error('Debe especificar la cuenta contable de ingreso.');
      }

      let contact = PreinvoiceFormUtils.resolveSelected(this.contacts, fv.contactUid);
      if (!contact || !contact.id) throw new Error('Debe especificar un contacto válido.');

      let payType = self.form.get('payMethodUid').value;
      if (!payType) throw new Error('Debe especificar la forma de cobro.');

      let bank = PreinvoiceFormUtils.resolveSelected(this.bankAccounts, fv.bankAccountUid);
      let cash = fv.cashRegisterUid != null && this.cashRegisters.filter(c => c.id == fv.cashRegisterUid.id)[0] != null ? { id: this.cashRegisters.filter(c => c.id == fv.cashRegisterUid.id)[0].metadata.id } : null;
      let partner = PreinvoiceFormUtils.resolveSelected(this.partners, fv.partnerUid);
      let project = PreinvoiceFormUtils.resolveSelected(this.projects, fv.projectUid);
      let accountingModel = PreinvoiceFormUtils.resolveSelected(this.accountingModels, fv.entranceAccount);
      let center = PreinvoiceFormUtils.resolveSelected(this.centers, fv.centerUid);

      if (this.isBankAccountPayment() && (!bank || !bank.id)) throw new Error('Debe especificar la cuenta bancaria.');
      if (this.isCashRegisterPayment() && (!cash || !cash.id)) throw new Error('Debe especificar la caja/efectivo.');
      if (this.isPartnerPayment() && (!partner || !partner.id)) throw new Error('Debe especificar el socio.');

      // 1) Mapper tuyo (títulos + líneas -> líneas mapeadas)
      let mapped = PreinvoiceSaveMapper.mapItemsToGroupsAndLines({
        items: fv.items,
        concepts: this.concepts,
        taxes: this.taxes
      });

      // 2) Construir invoiceLines EXACTO formato API
      let invoiceLines = this.buildApiInvoiceLines(mapped.invoiceLines, fv.items, fv.isMeasurable);

      // 3) DTO final como el JSON que pasaste
      let dto: any = {
        invoiceLines: invoiceLines,

        preInvoiceNumber: fv.preInvoiceNumber || '',
        invoiceRelease: fv.invoiceRelease,
        invoiceExpiration: fv.invoiceExpiration,

        issuerAddress: fv.issuerAddress,
        issuerPostalCode: fv.issuerPostalCode,
        issuerLocation: fv.issuerLocation,
        issuerCountry: fv.issuerCountry,
        issuerState: fv.issuerState,
        issuerName: fv.issuerName,
        issuerVatNumber: fv.issuerVatNumber,
        comments: fv.comments,

        entranceAccount: accountingModel ? accountingModel.display : (fv.entranceAccount && typeof fv.entranceAccount === 'object' ? (fv.entranceAccount.display || fv.entranceAccount.id || null) : fv.entranceAccount),

        receiverName: (contact.display || contact.name || 'Factura simplificada'),
        receiverUid: contact.id,

        centerUid: center ? center.id : '00000000-0000-0000-0000-000000000000',

        expenseAccount: null,

        typePay: payType.id,

        partnersId: partner ? partner.id : 0,
        projectUid: project ? project.id : null,

        useDeliverAddress: fv.useDeliverAddress === true,

        numberKilometers: (fv.numberKilometers === '' ? null : fv.numberKilometers),

        fiscalYear: Number(ActualSelected.yearSelected),
        activityUid: ActualSelected.activitySelected,

        total: Number(this.total) || 0
      };

      if (this.isBankAccountPayment() && bank) {
        dto.typePayBankAccount = bank.id;
      }

      if (this.isCashRegisterPayment() && cash) {
        dto.efectivoId = cash.id;
      }

      dto.invoiceLineGroups = mapped.lineGroups;

      let p: Promise<any>;
      if (!fv.uid) p = this.preinvoiceService.addData(dto);
      else p = this.preinvoiceService.editData(fv.uid, dto);

      this.loading.run(fv.uid ? 'Guardando presupuesto…' : 'Creando presupuesto…', p).then(function () {
        self.isSaving = false;
        self.notification.okBottom('Presupuesto guardado correctamente');
        self.router.navigate(['invoice/preinvoice']);
        self.close();
      }).catch(function (e: any) {
        self.isSaving = false;
        console.error(e);
        self.notification.errorBottom(e && e.message ? e.message : 'Error guardando el presupuesto.');
      });

    } catch (e) {
      this.isSaving = false;
      console.error(e);
      this.notification.errorBottom(e && e.message ? e.message : 'Error guardando el presupuesto.');
    }
  }

  private buildApiInvoiceLines(mappedLines: any[], uiItems: any[], isMeasurableFlag: boolean): any[] {
    let result: any[] = [];

    // líneas UI (sin títulos) para coger measurable + selección taxes "tal cual"
    let uiLines: any[] = [];
    for (let i = 0; i < (uiItems || []).length; i++) {
      if (uiItems[i] && uiItems[i].type === 'line') uiLines.push(uiItems[i]);
    }

    for (let pos = 0; pos < (mappedLines || []).length; pos++) {
      let ml = mappedLines[pos];
      let ui = (pos < uiLines.length) ? uiLines[pos] : null;

      let qty = Number(ml.quantity) || 0;
      let unityAmount = Number(ml.unityAmount) || 0;
      let discount = Number(ml.discount) || 0;

      // En tu ejemplo netPrice=unityAmount*quantity (sin descuento)
      let netPrice = unityAmount * qty;

      // taxes: en tu ejemplo taxImport es "21" y "7" (parece %)
      // Por eso mandamos taxImport como RATE (metadata.import), no como importe calculado.
      let apiTaxes = this.buildApiTaxesFromUiSelection(ui ? ui.tax : null);

      let lineDto: any = {
        product: ml.product,
        conceptUid: ml.conceptUid,

        quantity: qty,
        discount: discount,
        unityAmount: unityAmount,

        netPrice: netPrice,

        position: pos,
        totalLine: Number(ml.totalLine) || 0,

        taxes: apiTaxes,

        invoiceLineGroup: ml.groupClientKey,
      };

      // measurable exactamente como tu JSON
      if (isMeasurableFlag === true) {
        lineDto.isMeasurable = true;
        lineDto.masterUnitMeasurableUid = ui && ui.masterUnitMeasurableUid ? ui.masterUnitMeasurableUid.id : null;

        // tu ejemplo lo manda como string
        if (ui && ui.quantityMeasurable != null) lineDto.quantityMeasurable = String(ui.quantityMeasurable);
        else lineDto.quantityMeasurable = null;
      } else {
        lineDto.masterUnitMeasurableUid = null;
        lineDto.isMeasurable = false;
        lineDto.quantityMeasurable = 1;
      }

      result.push(lineDto);
    }

    return result;
  }

  private buildApiTaxesFromUiSelection(selectedTaxes: any[]): any[] {
    let res: any[] = [];
    let sel = selectedTaxes || [];

    for (let i = 0; i < sel.length; i++) {
      let t = sel[i];
      if (!t) continue;

      let pickedId = null;
      if (t.id != null) pickedId = t.id;
      else if (t.uid != null) pickedId = t.uid;
      else if (t.value != null) pickedId = t.value;

      // buscar en lookups taxes por id (si pickedId coincide con id)
      let full = null;
      for (let j = 0; j < (this.taxes || []).length; j++) {
        if (this.taxes[j] && this.taxes[j].id === pickedId) { full = this.taxes[j]; break; }
      }

      let rate = 0;
      let type = '';
      let alias = '';
      let name = '';
      let taxId = null;

      if (full) {
        alias = full.display || '';
        if (full.metadata) {
          if (full.metadata.import != null) rate = full.metadata.import;
          if (full.metadata.type) type = full.metadata.type;
          if (full.metadata.id != null) taxId = full.metadata.id;
          if (full.metadata.name != null) name = full.metadata.name;
        }
      }

      // fallback si no encontramos full
      if (!alias && t.alias) alias = t.alias;
      if (!type && t.type) type = t.type;

      let dto: any = {
        name: name,
        alias: alias,
        taxImport: rate,     // ✅ como tu ejemplo: 21 / 7
        type: type,
        taxId: taxId,
        id: taxId,
        uid: full.id
      };

      // mantener uid/id si existen, para que se parezca al ejemplo
      if (t.uid) dto.uid = t.uid;
      if (t.id != null) dto.id = taxId;

      res.push(dto);
    }

    return res;
  }


  // Totales simples (sin duplicar mucha lógica)
  // get subtotal(): number {
  //   var vals = this.items.value || [];
  //   var sum = 0;
  //   for (var i = 0; i < vals.length; i++) {
  //     if (!vals[i] || vals[i].type !== 'line') continue;
  //     var qty = Number(vals[i].quantity) || 0;
  //     var price = Number(vals[i].price) || 0;
  //     var disc = Number(vals[i].discount) || 0;
  //     sum += price * qty * (1 - disc / 100);
  //   }
  //   return sum;
  // }

  // get taxesTotal(): number {
  //   var vals = this.items.value || [];
  //   var sum = 0;
  //   for (var i = 0; i < vals.length; i++) {
  //     if (!vals[i] || vals[i].type !== 'line') continue;

  //     var qty = Number(vals[i].quantity) || 0;
  //     var price = Number(vals[i].price) || 0;
  //     var disc = Number(vals[i].discount) || 0;
  //     var base = price * qty * (1 - disc / 100);

  //     var taxes = vals[i].tax || [];
  //     for (var j = 0; j < taxes.length; j++) {
  //       var full = this.findTaxFull(taxes[j]);
  //       var rate = (full && full.metadata && full.metadata.import != null) ? full.metadata.import : 0;
  //       if (rate > 0) sum += base * (rate / 100);
  //     }
  //   }
  //   return sum;
  // }

  // get retentionsTotal(): number {
  //   var vals = this.items.value || [];
  //   var sum = 0;
  //   for (var i = 0; i < vals.length; i++) {
  //     if (!vals[i] || vals[i].type !== 'line') continue;

  //     var qty = Number(vals[i].quantity) || 0;
  //     var price = Number(vals[i].price) || 0;
  //     var disc = Number(vals[i].discount) || 0;
  //     var base = price * qty * (1 - disc / 100);

  //     var taxes = vals[i].tax || [];
  //     for (var j = 0; j < taxes.length; j++) {
  //       var full = this.findTaxFull(taxes[j]);
  //       var rate = (full && full.metadata && full.metadata.import != null) ? full.metadata.import : 0;
  //       if (rate < 0) sum += base * (rate / 100);
  //     }
  //   }
  //   return sum;
  // }

  // get total(): number {
  //   return this.subtotal + this.taxesTotal + this.retentionsTotal;
  // }
  private isMeasurableEnabled(): boolean {
    if (!this.form) return false;
    let c = this.form.get('isMeasurable');
    return !!(c && c.value === true);
  }

  private getBaseForLine(v: any): number {
    if (!v || v.type !== 'line') return 0;

    let qty = Number(v.quantity) || 0;
    let price = Number(v.price) || 0;
    let disc = Number(v.discount) || 0;

    let measurable = this.isMeasurableEnabled();

    let qm = 1;
    if (measurable) {
      qm = Number(v.quantityMeasurable);
      if (!qm || qm <= 0) qm = 1;
    }

    let qtyReal = measurable ? (qty * qm) : qty;

    return price * qtyReal * (1 - disc / 100);
  }

  recomputeTotals(): void {
    const vals = (this.items && this.items.value) ? this.items.value : [];

    let subtotal = 0;
    let taxesTotal = 0;
    let retentionsTotal = 0;
    let hasIVA = false;
    let hasIGIC = false;
    let hasAIEM = false;
    const lineTotals: number[] = [];

    for (let i = 0; i < vals.length; i++) {
      const v = vals[i];
      if (!v || v.type !== 'line') {
        lineTotals.push(0);
        continue;
      }

      const base = this.getBaseForLine(v);
      subtotal += base;

      let lineSum = base;
      const taxes = v.tax || [];
      for (let j = 0; j < taxes.length; j++) {
        const full = this.findTaxFull(taxes[j]);
        const rate = full && full.metadata && full.metadata.import != null ? full.metadata.import : 0;
        const amount = base * (rate / 100);
        lineSum += amount;

        if (rate > 0) taxesTotal += amount;
        else if (rate < 0) retentionsTotal += amount;

        if (full && full.metadata && full.metadata.type != null) {
          const text = String(full.metadata.type).toUpperCase();
          if (text.indexOf('AIEM') !== -1) hasAIEM = true;
          if (text.indexOf('IGIC') !== -1) hasIGIC = true;
          if (text.indexOf('IVA') !== -1) hasIVA = true;
        }
      }

      lineTotals.push(lineSum);
    }

    this.subtotal = subtotal;
    this.taxesTotal = taxesTotal;
    this.retentionsTotal = retentionsTotal;
    this.total = subtotal + taxesTotal + retentionsTotal;
    this.lineTotals = lineTotals;
    this.taxesLabel = hasIVA ? 'IVA' : hasIGIC ? 'IGIC' : hasAIEM ? 'AIEM' : 'Impuestos';
  }

  private wireTotals(): void {
    this.form.valueChanges
      .pipe(debounceTime(150), takeUntil(this.destroy$))
      .subscribe(() => this.recomputeTotals());
  }

  private buildTaxIndex(): void {
    this.taxById = new Map<any, any>();
    for (const tax of this.taxes || []) {
      if (tax && tax.id != null) this.taxById.set(tax.id, tax);
    }
  }

  // getLineTotal(itemValue: any): number {
  //   if (!itemValue || itemValue.type !== 'line') return 0;

  //   var qty = Number(itemValue.quantity) || 0;
  //   var price = Number(itemValue.price) || 0;
  //   var disc = Number(itemValue.discount) || 0;
  //   var base = price * qty * (1 - disc / 100);

  //   var sum = base;
  //   var taxes = itemValue.tax || [];
  //   for (var j = 0; j < taxes.length; j++) {
  //     var full = this.findTaxFull(taxes[j]);
  //     var rate = (full && full.metadata && full.metadata.import != null) ? full.metadata.import : 0;
  //     sum += base * (rate / 100);
  //   }
  //   return sum;
  // }

  getLineTotal(itemValue: any): number {
    if (!itemValue || itemValue.type !== 'line') return 0;

    let qty = Number(itemValue.quantity) || 0;
    let price = Number(itemValue.price) || 0;
    let disc = Number(itemValue.discount) || 0;

    let measurable = false;

    if (this.form && this.form.get('isMeasurable') && this.form.get('isMeasurable').value === true) {
      measurable = true;
    } else if (itemValue.isMeasurable === true) {
      measurable = true;
    } else if (itemValue.masterUnitMeasurableUid) {
      measurable = true;
    }

    let qm = 1;
    if (measurable) {
      qm = Number(itemValue.quantityMeasurable);
      if (!qm || qm <= 0) qm = 1;
    }

    let qtyReal = measurable ? (qty * qm) : qty;

    let base = price * qtyReal * (1 - disc / 100);

    let sum = base;

    let taxes = itemValue.tax || [];
    for (let j = 0; j < taxes.length; j++) {
      let full = this.findTaxFull(taxes[j]);
      let rate = 0;
      if (full && full.metadata && full.metadata.import != null) rate = full.metadata.import;
      sum += base * (rate / 100);
    }

    return sum;
  }

  private findTaxFull(tx: any): any {
    const id = PreinvoiceFormUtils.pickId(tx);
    if (id == null) return null;
    const found = this.taxById.get(id);
    return found != null ? found : null;
  }

  // number
  private tryGenerateNumber(): void {
    let self = this;
    // if (!this.preinvoiceService.generatePreinvoiceNumber) return;

    // this.preinvoiceService.generatePreinvoiceNumber()
    //   .then(function (num: any) {
    //     var ctrl = self.form.get('preInvoiceNumber');
    //     if (ctrl) ctrl.setValue(num);
    //   })
    //   .catch(function () { });
  }

  private loadExisting(uid: string): void {
    // si lo necesitas, lo metemos aquí; ahora lo dejo minimal.
  }

  expand(): void { }
  onCancel(): void {
    if (this.dialogRef) {
      this.dialogRef.close();
      return;
    }
    this.router.navigate(['invoice/preinvoice']);
  }

  private close(): void {
    if (this.dialogRef) this.dialogRef.close();
    else this.dialog.closeAll();
  }

  onConceptChange(line: any) {
    let self = this;

    const concept = self.concepts.filter(c => c.id == line.get('conceptUid').value.id)[0] as MasterConceptModel;

    if (concept) {
      line.get('serieConceptUid').setValue(concept.id);

      if (concept.metadata.serialNumber) {
        this.conceptService.loadData(concept.id).then(response => {
          const dialogRef = this.dialog.open(ConceptSerialNumberComponent, {
            width: '640px',
            maxWidth: '96vw',
            height: '70%',
            data: {
              concept: response,
              live: true
            }
          });

          dialogRef.afterClosed().subscribe(result => {
            if (result) {
              // No sobrescribir el control con un string plano: perdería el
              // `id` del concepto y la línea se guardaría sin concepto
              // vinculado. Se conserva el id y solo se actualiza el texto.
              const current = line.get('conceptUid').value;
              line.get('conceptUid').setValue({ ...current, display: current.display + " - S/N: " + result });
            }
          });
        });
      }

      // line.get('masterUnitMeasurableUid').setValue(null);
      // line.get('quantityMeasurable').setValue(1);
      line.get('quantity').setValue(1);
      line.get('price').setValue(concept.metadata.netPrice);
      line.get('discount').setValue(concept.metadata.discount);

      const conceptTaxes = [];

      concept.metadata.taxes.forEach(t => {
        const tax = self.taxes.filter(c => c.metadata.id == t.taxId)[0];
        if (tax) conceptTaxes.push(tax);
      });

      // Si el concepto no tiene taxes, aplicar los taxes por defecto del contacto
      if (conceptTaxes.length === 0) {

        if (self.contactTaxClientUid) {
          const tax1 = self.taxes.filter(t => t.id === self.contactTaxClientUid)[0];
          if (tax1) conceptTaxes.push(tax1);
        }
        if (self.contactTaxClientSecondUid) {
          const tax2 = self.taxes.filter(t => t.id === self.contactTaxClientSecondUid)[0];
          if (tax2) conceptTaxes.push(tax2);
        }
      }

      line.get('tax').setValue(conceptTaxes);
    } else {
      // No se ha encontrado un concepto (texto libre): aplicar igualmente los taxes del contacto.
      const contactTaxes = self.getContactDefaultTaxes();
      if (contactTaxes.length > 0) {
        line.get('tax').setValue(contactTaxes);
      }
    }
  }

  /** Devuelve los taxes por defecto definidos en el contacto seleccionado (1º y 2º). */
  private getContactDefaultTaxes(): any[] {
    const taxes: any[] = [];
    if (this.contactTaxClientUid) {
      const tax1 = this.taxes.filter(t => t.id === this.contactTaxClientUid)[0];
      if (tax1) taxes.push(tax1);
    }
    if (this.contactTaxClientSecondUid) {
      const tax2 = this.taxes.filter(t => t.id === this.contactTaxClientSecondUid)[0];
      if (tax2) taxes.push(tax2);
    }
    return taxes;
  }

  onPaymethodChange() {
    this.applyPaymentValidators();
  }

  onContactChange(selectedValue: any) {
    const contact = this.contacts.filter(c => c.id == selectedValue.id)[0];

    if (contact) {
      this.assingDataContact();
      return;
    }

    ContactFormV2Component.open(this.dialog, { scratch: { companyName: selectedValue.id, isClient: true } })
      .pipe(
        delay(1200)
      )
      .subscribe(result => {
        this.lookupsFacade.loadContacts$()
          .pipe(takeUntil(this.destroy$))
          .subscribe((list: any[]) => {
            this.contacts = list || [];

            const contact = this.contacts.filter(c => result && result.id && String(c.id).toLowerCase() === String(result.id).toLowerCase())[0]
              || this.contacts.filter(c => c.id == selectedValue.id)[0];

            if (result && result.id && contact) {
              this.form.patchValue({
                contactUid: {
                  id: contact.id,
                  name: contact.display
                }
              });
              this.assingDataContact();
            }
          });
      });
  }

  assingDataContact() {
    let self = this;

    const contactRequest = this.contactService.loadData(self.form.get('contactUid').value.id)
      .then(response => this.ensurePaymentLookups(response ? response.paymentTypeClient : '').then(() => response));

    this.loading.run('Cargando datos del contacto…', contactRequest).then(response => {
      try {
        const contactSelected = response;
        const entranceAccount = response.entranceAccount;

        let account = null;
        if (Utils.IsNullOrEmpty(entranceAccount)) {
          if (self.userCompany.isSalesInvoice) {
            account = self.accountingModels.filter(c => c.metadata != null && c.metadata.number == '7000000001')[0];
          }

          if (self.userCompany.isServiceInvoice) {
            account = self.accountingModels.filter(c => c.metadata != null && c.metadata.number == '7050000001')[0];
          }

          if (self.userCompany.isSalesInvoice && self.userCompany.isServiceInvoice) {
            account = self.accountingModels.filter(c => c.metadata != null && c.metadata.number == '7000000001')[0];
          }
        } else {
          account = self.accountingModels.filter(c => c.display == response.entranceAccount)[0];
        }

        if (account) {
          self.form.patchValue({ entranceAccount: { id: account.id, display: account.display } });
        }

        self.form.patchValue({ payMethodUid: { id: response.paymentTypeClient, display: response.paymentTypeClient } });

        if (contactSelected.paymentClientUid != null && self.isBankAccountPayment()) {
          const bankAccount = self.bankAccounts.filter(c => c.id == response.paymentClientUid)[0];
          self.form.patchValue({ bankAccountUid: { id: bankAccount.id, display: bankAccount.display } });
        }

        if (contactSelected.clientEfectivoId != null && self.isCashRegisterPayment()) {
          const cashRegister = self.cashRegisters.filter(c => c.metadata.id == response.clientEfectivoId)[0];
          self.form.patchValue({ cashRegisterUid: { id: cashRegister.id, display: cashRegister.display } });
        }

        if (contactSelected.clientPartnersId != null && self.isPartnerPayment()) {
          const partner = self.partners.filter(c => c.metadata.id == response.clientPartnersId.toString())[0];
          self.form.patchValue({ partnerUid: { id: partner.id, display: partner.display } });
        }

        self.contactExpirationDays = contactSelected.expirationClient != null ? Number(contactSelected.expirationClient) : null;
        const release = self.form.get('invoiceRelease').value;
        if (release && moment(release).isValid()) {
          self.form.get('invoiceExpiration').setValue(moment(release).add(self.contactExpirationDays || 0, 'days').toDate());
        }

        // Guardar los taxes por defecto del contacto
        self.contactTaxClientUid = contactSelected.taxClientUid || null;
        self.contactTaxClientSecondUid = contactSelected.taxClientSecondUid || null;
      } catch (error) {
        console.error(error);
        this.notification.errorBottom('Se ha producido un error al asignar los datos del contacto. Por favor, revise y complete la información requerida.')
      }

      self.loadCentersForContact(self.form.get('contactUid').value.id);
    }, () => {
      self.notification.errorBottom('No se pudieron cargar los datos del contacto.');
    });
  }

  loadCentersForContact(contactUid: string) {
    let self = this;
    if (!contactUid) {
      self.centers = [];
      return;
    }

    this.lookupsFacade.loadCenters$(contactUid)
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        function (list: any[]) {
          self.centers = list || [];
          // Si solo hay un centro, seleccionarlo automáticamente
          if (self.centers.length === 1) {
            self.form.patchValue({ centerUid: { id: self.centers[0].id, display: self.centers[0].display } });
          }
        },
        function (err: any) {
          console.error('Error cargando centros:', err);
          self.centers = [];
        }
      );
  }

  private loadCentersForContactAsync(contactUid: string): Promise<void> {
    let self = this;
    if (!contactUid) {
      self.centers = [];
      return Promise.resolve();
    }

    return new Promise((resolve, reject) => {
      this.lookupsFacade.loadCenters$(contactUid)
        .pipe(takeUntil(this.destroy$))
        .subscribe(
          function (list: any[]) {
            self.centers = list || [];
            resolve();
          },
          function (err: any) {
            console.error('Error cargando centros:', err);
            self.centers = [];
            resolve(); // Resolver aunque falle para no bloquear
          }
        );
    });
  }

  loadActivity() {
    let self = this;

    this.activityService.loadData(ActualSelected.activitySelected).then(activity => {
      self.form.get('preInvoiceNumber').setValue(activity.preinvoiceNumber);
      self.form.get('applyProject').setValue(activity.isProject);
      if (activity.isProject) self.ensureProjects();
      self.isLoading = false;
    });
  }

  toggleUseDeliverAddress(checked: boolean) {
    let self = this;
    if (!checked) return;

    const contact = self.form.get('contactUid').value;
    if (!contact || !contact.id) {
      self.showMessage("Selecciona primero un contacto.", "Cerrar", "warn");
      self.form.get('useDeliverAddress').setValue(false);
      return;
    }

    this.contactService.loadData(contact.id).then(response => {
      const contactSelected = response;

      if (!contactSelected || !contactSelected.deliverAddress || contactSelected.deliverAddress.trim() === '') {
        self.showMessage("El contacto seleccionado no dispone de domicilio de entrega, puede cumplimentarlo en la ficha Cliente en Contactos.", "Cerrar", "warn");
        this.form.get('useDeliverAddress').setValue(false);
        return;
      }

      this.form.get('useDeliverAddress').setValue(true);
    });
  }

  private lastValidTaxesByLineKey: any = {};
  private prevTaxesByLineKey: any = {};

  private getLineKey(line: any): string {
    if (line && line.value && line.value.uid) return String(line.value.uid);

    if (line && !line.__taxKey) {
      line.__taxKey = 'l_' + Math.random().toString(16).slice(2) + '_' + new Date().getTime();
    }
    return line ? String(line.__taxKey) : 'unknown';
  }

  // Debe llamarse ANTES de que el usuario cambie el valor de impuestos (por ejemplo, en (focus) del control)
  handleTaxFocus = (lineIndex: number): void => {
    const line = this.items.at(lineIndex);
    if (!line) return;
    let c = line.get('tax');
    if (!c) return;
    let key = this.getLineKey(line);
    // Copia profunda para evitar referencias
    this.prevTaxesByLineKey[key] = JSON.parse(JSON.stringify(c.value || []));
  }

  handleTaxChange = (value: any, lineIndex: number): string => {
    const line = this.items.at(lineIndex);
    if (!line) return '';

    let c = line.get('tax');
    if (!c) return '';

    let key = this.getLineKey(line);
    let current = c.value || [];

    // Si no hay valor anterior guardado, usar el actual como "válido"
    if (!this.lastValidTaxesByLineKey[key]) {
      this.lastValidTaxesByLineKey[key] = JSON.parse(JSON.stringify(current));
    }

    let validation = this.validateTaxesSelection(current);

    if (!validation.ok) {
      // Restaurar el valor anterior inmediato (antes del cambio)
      if (this.prevTaxesByLineKey[key]) {
        c.setValue(JSON.parse(JSON.stringify(this.prevTaxesByLineKey[key])), { emitEvent: true });
      } else {
        c.setValue(JSON.parse(JSON.stringify(this.lastValidTaxesByLineKey[key])), { emitEvent: true });
      }
      if (validation.message) this.notification.errorBottom(validation.message);
      return '';
    }

    // Guardar el valor válido actual
    this.lastValidTaxesByLineKey[key] = JSON.parse(JSON.stringify(current));
    return '';
  }

  private validateTaxesSelection(selected: any[]): { ok: boolean; message: string } {
    let list = selected || [];

    let used: any = {};
    let mainTypeUsed: any = {};

    for (let i = 0; i < list.length; i++) {
      let tx = list[i];
      if (!tx) continue;

      let id = this.getTaxId(tx);
      if (id == null) continue;

      if (used[id]) {
        return { ok: false, message: 'Este impuesto ya ha sido aplicado.' };
      }

      let type = this.getTaxType(tx);
      let isRecargo = this.isRecargoEquivalencia(tx);

      if (!isRecargo && type) {
        if (mainTypeUsed[type]) {
          return { ok: false, message: 'Ya se ha aplicado un impuesto del tipo ' + type + '.' };
        }
        mainTypeUsed[type] = true;
      }

      used[id] = true;
    }

    return { ok: true, message: '' };
  }

  private getTaxId(t: any): any {
    if (!t) return null;
    if (t.id != null) return t.id;
    if (t.uid != null) return t.uid;
    if (t.value != null) return t.value;
    return null;
  }

  private getTaxName(t: any): string {
    if (!t) return '';
    if (t.name) return String(t.name);
    if (t.display) return String(t.display);
    return '';
  }

  private getTaxType(t: any): string {
    if (!t) return '';
    if (t.type) return String(t.type);
    if (t.metadata && t.metadata.type) return String(t.metadata.type);
    return '';
  }

  private isRecargoEquivalencia(t: any): boolean {
    return t.metadata.name.toLowerCase().indexOf('recargo equiv') >= 0 || t.metadata.name.toLowerCase().indexOf('recargo de equiv') >= 0;
  }

  // TrackBy para optimizar *ngFor
  trackByIndex(index: number): number {
    return index;
  }
}


